import { useMemo, useState } from "react";

import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { OpportunityDetailsResponse } from "lib/generated/integration-api";
import { useConnectedParagonCrm } from "lib/hooks/api/integrations/useConnectedParagonCrm";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useOpportunityCrmDetails } from "lib/hooks/api/integrations/useOpportunityCrmDetails";
import { EventNames } from "lib/tracking";
import { SendToCRMButton, ViewInCRMButton } from "./ProviderButtons";
import { NativeProviderType } from "./providerMetadata";
import { Opportunity, SendOpportunityToCRMModal } from "./SendOpportunityToCRMModal";
import { SendToParagonCrm } from "./SendToParagonCrmButton";
import type { CrmBidFields, CrmPerson, CrmRecord } from "./SendToParagonCrmModal";

type SendOpportunityToCRMButtonProps = {
  opportunity: Opportunity;
  providerType: NativeProviderType;
  crmDetails?: OpportunityDetailsResponse;
};

export function SendOpportunityToCRM({
  opportunity,
  allowParagonCrms,
  bid,
  people,
}: {
  opportunity: Opportunity;
  /**
   * Offers the CRM the company connected through Paragon rather than through an OAuth app of ours.
   * Opt in per call site so the Paragon SDK is only loaded where one is offered, and only takes
   * effect for a company whose flag for that CRM is on.
   */
  allowParagonCrms?: boolean;
  /**
   * The bid fields a mapping can reach, which marks the object being pushed as a bid. The providers
   * we run OAuth for build their form on the server and take none of it.
   */
  bid?: CrmBidFields;
  /**
   * The contacts saved to this record. A mapping that writes to an object holding a person takes
   * their details from whichever of these the user chooses, rather than asking them to be typed.
   */
  people?: CrmPerson[];
}): JSX.Element | null {
  const { data: providerTypeResponse, isLoading: isLoadingProvider } = useFindIntegrationProvider();
  // Opt-in, so a call site that does not offer a Paragon CRM never authenticates its SDK.
  const paragonCrm = useConnectedParagonCrm(allowParagonCrms);
  const providerType = providerTypeResponse?.providerType;
  const { data: opportunityCrmDetails, isInitialLoading: isLoadingOpportunityCrmDetails } =
    useOpportunityCrmDetails(opportunity.guid, {
      enabled: !!providerType,
    });

  // Memoised because the modal seeds its form from this, and the pages build `opportunity` inline.
  const crmRecord: CrmRecord = useMemo(
    () => ({
      kind: bid ? "bid" : "lead",
      guid: opportunity.guid,
      name: opportunity.name,
      stotlesRoute: opportunity.stotlesRoute,
      value: opportunity.value,
      // A lead's close date is its expiry date; a bid's own dates come through `bid`.
      expiryDate: opportunity.closeDate,
      ...bid,
    }),
    [
      bid,
      opportunity.guid,
      opportunity.name,
      opportunity.stotlesRoute,
      opportunity.value,
      opportunity.closeDate,
    ],
  );

  if (isLoadingProvider || isLoadingOpportunityCrmDetails) {
    return <Button buttonType="secondary" loading />;
  }

  const connectPrompt = (
    <Button buttonType="secondary" as="a" {...appRoutes.integrations} icon="plus">
      Send to CRM
    </Button>
  );

  if (providerType) {
    return (
      <div>
        <SendOpportunityToCRMButton
          opportunity={opportunity}
          providerType={providerType}
          crmDetails={opportunityCrmDetails}
        />
      </div>
    );
  }

  // One CRM at a time, so the install names which one rather than each arm being tried in turn.
  if (allowParagonCrms && paragonCrm) {
    return (
      <div>
        <SendToParagonCrm
          provider={paragonCrm}
          record={crmRecord}
          people={people}
          fallback={connectPrompt}
        />
      </div>
    );
  }

  return <div>{connectPrompt}</div>;
}

function SendOpportunityToCRMButton({
  opportunity,
  crmDetails,
  providerType,
}: SendOpportunityToCRMButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onOpenChange = (open: boolean) => {
    setIsModalOpen(open);
  };

  if (crmDetails?.externalUrl) {
    return <ViewInCRMButton externalUrl={crmDetails.externalUrl} providerType={providerType} />;
  }

  return (
    <>
      <SendToCRMButton
        providerType={providerType}
        onClick={() => onOpenChange(true)}
        eventName={EventNames.sendToCRMModalOpened}
      />
      <SendOpportunityToCRMModal
        open={isModalOpen}
        onOpenChange={onOpenChange}
        opportunity={opportunity}
        providerType={providerType}
      />
    </>
  );
}
